import { graphql, useStaticQuery } from "gatsby"
import { gsap } from "gsap"
import { ReactComponent as ChevDownSVG } from "images/hero/chevDown.svg"
import UniversalImage from "library/UniversalImage"
import { fmobile, fresponsive, ftablet } from "library/fullyResponsive"
import useAnimation from "library/useAnimation"
import useMedia from "library/useMedia"
import { useRef, useState } from "react"
import styled, { css } from "styled-components"
import colors from "styles/colors"
import textStyles from "styles/text"
import VideoModal from "./VideoModal"
import SecondaryCTA from "./buttons/SecondaryCTA"

export default function HeroGraphic() {
	const mobile = useMedia(false, false, false, true)
	const departmentsRef = useRef<HTMLDivElement>(null)
	const locationsRef = useRef<HTMLDivElement>(null)
	const [videoModalIsOpen, setVideoModalIsOpen] = useState(false)

	const images: Queries.HeroImageQuery = useStaticQuery(graphql`
		query HeroImage {
			product: file(relativePath: { eq: "hero/HeroProduct.png" }) {
				childImageSharp {
					gatsbyImageData(placeholder: NONE)
				}
			}
			mobile: file(relativePath: { eq: "hero/MobileProduct.png" }) {
				childImageSharp {
					gatsbyImageData(placeholder: NONE)
				}
			}
			# all files under hero/ProductList/*.png
			list: allFile(
				filter: { relativePath: { glob: "hero/ProductList/*.png" } }
				sort: { relativePath: ASC }
			) {
				nodes {
					childImageSharp {
						gatsbyImageData(placeholder: NONE)
					}
					name
				}
			}
		}
	`)

	const departments = [
		"Fulfillment",
		"Operations",
		"Office",
		"Transport",
		"Maintenance",
	]

	const locations = [
		"New York, NY",
		"Salt Lake City, UT",
		"Fontana, CA",
		"Detroit, MI",
		"Columbus, OH",
	]

	const imagesPerSet = 10

	const imageSets = [0, 1, 2].map((setIndex) =>
		images.list.nodes.slice(
			setIndex * imagesPerSet,
			(setIndex + 1) * imagesPerSet,
		),
	)

	useAnimation(
		() => {
			if (mobile) return

			const productAnimation = (
				imageSetNumber: 1 | 2 | 3,
				departmentYPercent: number,
				locationYPercent: number,
			) => {
				const childTimeline = gsap.timeline()

				childTimeline.to(
					`.product-${imageSetNumber}`,
					{
						opacity: 1,
						duration: 0.5,
						stagger: -0.02,
						yPercent: -200,
						ease: "power1.out",
					},
					0,
				)

				childTimeline.to(
					`.product-${imageSetNumber}`,
					{
						opacity: 0,
						duration: 0.5,
						stagger: 0.02,
						yPercent: 0,
						ease: "power1.in",
					},
					4.5,
				)

				childTimeline.to(
					departmentsRef.current,
					{
						yPercent: departmentYPercent,
						duration: 1,
						ease: "power1.out",
					},
					4.5,
				)

				childTimeline.to(
					locationsRef.current,
					{
						yPercent: locationYPercent,
						duration: 1,
						ease: "power1.out",
					},
					4.5,
				)

				return childTimeline
			}

			const parentTimeline = gsap.timeline({ repeat: -1, delay: 1 })

			parentTimeline.add(productAnimation(1, -86, 43))
			parentTimeline.add(productAnimation(2, -43, 86))
			parentTimeline.add(productAnimation(3, 0, 0))
		},
		[mobile],
		{ recreateOnResize: true },
	)

	return (
		<Wrapper>
			<VideoModal isOpen={videoModalIsOpen} setIsOpen={setVideoModalIsOpen} />
			{!mobile ? (
				<>
					<BackgroundAccent>
						<Product className="product-image">
							<Background
								image={images.product}
								alt="a screenshot of Response's interface, showing products categorized by department and location"
							/>
							<DepartmentWrapper>
								<Departments ref={departmentsRef}>
									{departments.map((department) => (
										<Slot key={department}>
											{department}
											<ChevDown />
										</Slot>
									))}
								</Departments>
							</DepartmentWrapper>
							<LocationWrapper>
								<Locations ref={locationsRef}>
									{locations.map((location) => (
										<Slot key={location}>
											{location}
											<ChevDown />
										</Slot>
									))}
								</Locations>
							</LocationWrapper>
							{[0, 1, 2].map((index) => (
								<GridContent key={`grid-${index}`}>
									<ImageSet>
										{imageSets[index]?.map((node) => (
											<ProductImage
												key={node.name}
												image={node}
												alt=""
												className={`product-${index + 1}`}
											/>
										))}
									</ImageSet>
								</GridContent>
							))}
						</Product>
						{!mobile && (
							<VideoButton
								type="button"
								variant="withPlayButton"
								onClick={() => setVideoModalIsOpen(!videoModalIsOpen)}
							>
								Watch Demo
							</VideoButton>
						)}
					</BackgroundAccent>
				</>
			) : (
				<>
					<BackgroundAccent>
						<Background
							image={images.mobile}
							alt="a screenshot of Response's interface, showing products categorized by department and location"
						/>
					</BackgroundAccent>
					<VideoButton
						type="button"
						variant="withPlayButton"
						onClick={() => setVideoModalIsOpen(!videoModalIsOpen)}
					>
						Watch Demo
					</VideoButton>
				</>
			)}
		</Wrapper>
	)
}

const Wrapper = styled.div`
	${fresponsive(css`
		width: 1360px;
		height: auto;
		position: relative;
		overflow: clip;
		margin: 0 auto 47px;
		backdrop-filter: blur(4px);
		padding: 103px 0 59px;
	`)}

	${ftablet(css`
		width: 996px;
		padding: 0 0 40px;
	`)}

  ${fmobile(css`
		width: 358px;
		padding: 0 0 90px;
		overflow: visible;
		margin-bottom: 32px;
	`)}
`

const Background = styled(UniversalImage)`
	${fresponsive(css`
		width: 100%;
		height: auto;
	`)}

	${fmobile(css`
		position: absolute;
		width: 480px;
		top: 9px;
		left: 9px;
	`)}
`

const BackgroundAccent = styled.div`
	border: 1px solid ${colors.grey1};

	${fresponsive(css`
		width: 100%;
		height: 910px;
		border-radius: 30px;
		background: rgba(224 224 235 / 75%);
		backdrop-filter: blur(4px);
		position: relative;
	`)}

	${ftablet(css`
		height: 740px;
		display: flex;
		flex-direction: column;
		padding: 18px 19px 24px;
		align-items: center;
	`)}

	${fmobile(css`
		height: 330px;
		padding: 9px 0 24px 9px;
		border-radius: 12px;
		display: flex;
		align-items: center;
	`)}
`

const Product = styled.div`
	${fresponsive(css`
		width: 1198px;
		height: 774px;
		position: absolute;
		left: 50%;
		transform: translateX(-50%);
		top: 774px;
	`)}

	${ftablet(css`
		width: 958px;
		height: 619px;
		position: relative;
		top: 0;
		opacity: 0;
	`)}
`

const VideoButton = styled(SecondaryCTA)`
	${fresponsive(css`
		position: absolute;
		bottom: 32px;
		left: 81px;
	`)}

	${ftablet(css`
		left: unset;
		bottom: unset;
		margin-top: 25px;
		position: relative;
	`)}

	${fmobile(css`
		position: absolute;
		bottom: 0;
		left: 50%;
		translate: -50%;
	`)}
`

const GridContent = styled.div`
	${fresponsive(css`
		position: absolute;
		left: 110px;
		top: 175px;
		overflow: hidden;
	`)}

	${ftablet(css`
		top: 140px;
		left: 87px;
	`)}

	${fmobile(css`
		display: none;
	`)}
`

const ImageSet = styled.div`
	${fresponsive(css`
		display: grid;
		grid-template-columns: repeat(5, 1fr);
		grid-template-rows: repeat(2, 1fr);
		gap: 12px;
	`)}

	${ftablet(css`
		gap: 10px;
	`)}
`

const ProductImage = styled(UniversalImage)`
	${fresponsive(css`
		width: 200px;
		height: auto;
		opacity: 0;
		transform: translateY(200%);
	`)}

	${ftablet(css`
		width: 160px;
	`)}
`

const WrapperBase = css`
	${fresponsive(css`
		position: absolute;
		color: ${colors.grey4};
		${textStyles.h5};
		font-weight: 550;
		display: flex;
		align-items: center;
		white-space: nowrap;
		overflow: hidden;
		height: 38px;
		top: 105px;
	`)}

	${ftablet(css`
		${textStyles.h6};
		font-weight: 500;
		height: 30px;
		top: 80px;
	`)}

	${fmobile(css`
		display: none;
	`)}
`

const DepartmentWrapper = styled.div`
	${WrapperBase};
	${fresponsive(css`
		left: 114px;
	`)}

	${ftablet(css`
		left: 91px;
	`)}
`

const LocationWrapper = styled.div`
	${WrapperBase};
	${fresponsive(css`
		left: 343px;
	`)}

	${ftablet(css`
		left: 275px;
	`)}
`

const SlotsWrapperBase = css`
	${fresponsive(css`
		display: flex;
		flex-direction: column;
		gap: 16px;
	`)}

	${ftablet(css`
		gap: 13px;
	`)}
`

const Departments = styled.div`
	${SlotsWrapperBase};
	transform: translateY(42%);
`

const Locations = styled.div`
	${SlotsWrapperBase};
	transform: translateY(-44%);
`

const Slot = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
`

const ChevDown = styled(ChevDownSVG)`
	${fresponsive(css`
		width: 12px;
		height: 7px;
	`)}

	${ftablet(css`
		width: 9px;
		height: 6px;
	`)}

	${fmobile(css`
		display: none;
	`)}
`
